import { PropsWithChildren, useEffect, useMemo } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import { Map, MapProps } from '@vis.gl/react-google-maps';

import { MyLocationIcon } from '~assets/icons';
import { useGeolocationContext } from '~providers';
import { ICoordinate } from '~types';

type GoogleMapProps = {
  onChangeCurrentLocation: (coordinates: ICoordinate) => void;
} & MapProps;
export const GoogleMapComponent: React.FC<
  PropsWithChildren<GoogleMapProps>
> = ({ children, style, onChangeCurrentLocation, ...props }) => {
  const {
    googleMapKey,
    isGettingCurrentLocation,
    currentLocation,
    increaseGoogleMapKey,
    handleGetCurrentLocation,
  } = useGeolocationContext();

  const mapCenter = useMemo(() => {
    return currentLocation ? currentLocation : props.defaultCenter;
  }, [currentLocation, props.defaultCenter]);

  useEffect(() => {
    currentLocation &&
      onChangeCurrentLocation({
        latitude: currentLocation.lat,
        longitude: currentLocation.lng,
      });
    increaseGoogleMapKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Floating Geolocation Button */}
      <IconButton
        onClick={handleGetCurrentLocation}
        style={{
          position: 'absolute',
          width: 56,
          height: 56,
          bottom: 30,
          right: 20,
          zIndex: 1,
          backgroundColor: 'white',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        {isGettingCurrentLocation ? <CircularProgress /> : <MyLocationIcon />}
      </IconButton>

      {/* Map */}
      <Map
        key={googleMapKey}
        {...props}
        defaultZoom={20}
        mapId="DEMO_MAP_ID"
        style={style || { width: '100%', height: '400px' }}
        fullscreenControl={false}
        mapTypeControl={false}
        streetViewControl={false}
        zoomControl={false}
        defaultCenter={mapCenter}
      >
        {children}
      </Map>

      {/* Overlay - Drag location banner (Optional) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          padding: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          textAlign: 'center',
        }}
      >
        Drag the map to refine location
      </div>
    </div>
  );
};
